import { GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';

import {
  deleteOrIndexUser,
  getAllUsersCount,
  getUserCountByDomain,
  getUserCountByDomainBigQuery,
  getUserIndexLogsForDomain,
  indexAllUsers,
  indexUsersByDomain,
  UserKeys,
  getAllUsersCountBigQuery,
} from '../../../api/users-api';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useStore } from '../../state/zustand-store';
import { handleErrors } from '../../../utils/errors';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { IndexUserLog } from '../../../../types';
import { emailRegex } from '../../../utils/regular-expressions';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import ChangeLogDialog from '../../../shared/change-log-dialog';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PersonOff from '@mui/icons-material/PersonOff';
import Refresh from '@mui/icons-material/Refresh';
import SharedTabHeader from '../../../shared/shared-tab-header';

export default function UsersIndexingGrid() {
  const {
    administrationRoles,
    primaryDomain,
    allDomains,
    currentTier,
    features,
    changeLogRoles,
  } = useUserSettings();

  const [userEmail, setUserEmail] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(primaryDomain);
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar, userIndexingIsDisabled } = useStore();
  const [hasError, setHasError] = useState(false);
  const [radioValue, setRadioValue] = useState('User');
  const [fakeLoading, setFakeLoading] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const modifiedDomains = [...allDomains, 'All'];

  const enterpriseBackendIndexingEnabled =
    isFeatureEnabled('indexUsersReact', features) &&
    currentTier === 'enterprise';

  const {
    data: indexUserLogs,
    isLoading: isIndexUserLogsLoading,
    isFetching: isIndexUserLogsFetching,
  } = useQuery({
    queryKey: [UserKeys.IndexUserLog, selectedDomain],
    queryFn: () => getUserIndexLogsForDomain(selectedDomain),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('user index logs'),
    },
  });

  const { data: allUsersCount } = useQuery({
    queryKey: [UserKeys.AllUsersCount],
    queryFn: () =>
      enterpriseBackendIndexingEnabled
        ? getAllUsersCountBigQuery()
        : getAllUsersCount(),
  });

  const results = useQueries({
    queries: allDomains.map((domain) => ({
      queryKey: [UserKeys.UserCountByDomain, domain],
      queryFn: () =>
        enterpriseBackendIndexingEnabled
          ? getUserCountByDomainBigQuery(domain)
          : getUserCountByDomain(domain),
    })),
  });

  function handleNumberOfUsers(index: number, isAllUsers: boolean) {
    if (isAllUsers) {
      return allUsersCount?.toLocaleString('en', { useGrouping: true }) || '0';
    }

    const result = results[index];
    return result?.data?.toLocaleString('en', { useGrouping: true }) || '0';
  }

  const mostRecentRunTime = indexUserLogs?.reduce((latest, log) => {
    const logTime = new Date(log.lastRunTime);
    return logTime > latest ? logTime : latest;
  }, new Date(0));

  const { mutate, isPending } = useMutation({
    mutationFn: () => indexUsersByDomain(selectedDomain, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Indexing for users is now running.',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');

      queryClient.invalidateQueries({
        queryKey: [UserKeys.AllUsersCount],
      });
      queryClient.invalidateQueries({ queryKey: [UserKeys.UserCountByDomain] });
    },

    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue indexing the users. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.indexing.index_users_by_domain, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.indexing,
      });
    },
  });

  const { mutate: userMutate, isPending: isUserPending } = useMutation({
    mutationFn: () => deleteOrIndexUser(userEmail, changeLogMessage),
    onSuccess: (returnInfo) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          returnInfo === 'index'
            ? 'User has been indexed.'
            : 'User has been deleted from indexing.',
      });
      setOpenChangeLogDialog(false);
      setChangeLogMessage('');

      queryClient.invalidateQueries({
        queryKey: [UserKeys.AllUsersCount],
      });
      queryClient.invalidateQueries({ queryKey: [UserKeys.UserCountByDomain] });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue indexing the user. Please refresh and try again.',
      });
    },
    onSettled: () => {
      setUserEmail('');

      createEvent(analytics.administration.indexing.index_user, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.indexing,
      });
    },
  });

  const { mutate: mutateAllUsersAllDomains, isPending: isAllUsersPending } =
    useMutation({
      mutationFn: () => indexAllUsers(changeLogMessage),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'info',
          message: 'Index is now running.',
        });
        setOpenChangeLogDialog(false);
        setChangeLogMessage('');
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue indexing the users. Please refresh and try again.',
        });
      },
      onSettled: () => {
        createEvent(
          analytics.administration.indexing.index_all_users_in_all_domains,
          {
            module: ModuleList.modules.administration.name,
            section: ModuleList.modules.administration.sections.indexing,
          }
        );
      },
    });

  useEffect(() => {
    if (fakeLoading) {
      setTimeout(() => {
        setFakeLoading(false);
      }, 3000);
    }
  }, [fakeLoading]);

  useEffect(() => {
    if (!userIndexingIsDisabled) {
      setFakeLoading(false);
    }
  }, [userIndexingIsDisabled]);

  function handleDomainIndexing() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.RunUserIndexing.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      setFakeLoading(true);
      if (selectedDomain === 'All') {
        mutateAllUsersAllDomains();
      } else if (selectedDomain) {
        mutate();
      }
    }
  }

  function handleUserIndexing() {
    if (!emailRegex.test(userEmail)) {
      setHasError(true);
      return;
    }
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.RunUserIndexing.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      userMutate();
    }
  }

  const columns: GridColDef<IndexUserLog>[] = useMemo(
    () => [
      {
        field: 'lastRunTime',
        headerName: ' Last Run Time',
        width: 180,
        type: 'dateTime',
        valueGetter: (params: number) => {
          return new Date(params);
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'manual',
        headerName: 'Manual Run',
        type: 'boolean',
        width: 100,
      },
      {
        field: 'emailDomain',
        headerName: 'Email Domain',
        flex: 2,
      },
      {
        field: 'usersAdded',
        headerName: 'Users Added',
        flex: 1,
        align: 'center',
      },
      {
        field: 'usersDeleted',
        headerName: 'Users Deleted',
        flex: 1,
        align: 'center',
      },
      {
        field: 'usersUpdated',
        headerName: 'Users Updated',
        flex: 1,
        align: 'center',
      },
      {
        field: 'aliasesAdded',
        headerName: 'Aliases Added',
        flex: 1,
        align: 'center',
      },
      {
        field: 'aliasesDeleted',
        headerName: 'Aliases Deleted',
        flex: 1,
        align: 'center',
      },
    ],
    []
  );

  function CustomToolbar() {
    return (
      <SharedToolbar noColumn noFilter noQuickFilter>
        <Stack
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
        >
          Last Run Time:{' '}
          {mostRecentRunTime
            ? mostRecentRunTime.toLocaleString()
            : 'No runs yet'}
          <Tooltip arrow title="Refresh">
            <IconButton
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [UserKeys.IndexUserLog],
                });
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </SharedToolbar>
    );
  }

  const disabledByRole = !isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.RunUserIndexing.value
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Users" />
      <Box sx={{ px: 1 }}>
        <Stack gap={1} mb={2}>
          <Typography>
            {radioValue === 'User'
              ? 'Running an index on an individual user will add the user if they do not exist in gPanel, remove the user from gPanel if they are no longer in the domain, and update the user in gPanel if they have changed.'
              : 'Running an index on a domain will remove all users from search and user grid for that domain within gPanel and re-add them for the entire domain. There will be a period of time where users will not show up in search results or user grid while the index is running.'}
          </Typography>
          <Tooltip
            arrow
            title={
              disabledByRole
                ? `You need the permission "${AdministrationRoles.RunUserIndexing.name}" to perform this action.`
                : ''
            }
          >
            <Stack flexDirection="row" alignItems="flex-end">
              <FormControl sx={{ mt: 1 }} disabled={disabledByRole}>
                <FormLabel>
                  Select the type of indexing you would like to perform:
                </FormLabel>
                <RadioGroup
                  aria-labelledby="radio-buttons-group-label"
                  defaultValue="User"
                  name="radio-buttons-group"
                  value={radioValue}
                  onChange={(e) => {
                    setRadioValue(e.target.value);
                  }}
                >
                  <FormControlLabel
                    value="User"
                    control={<Radio />}
                    label="Individual User"
                  />
                  <FormControlLabel
                    value="Domain"
                    control={<Radio />}
                    label="All Users in Domain"
                  />
                </RadioGroup>
              </FormControl>

              <Stack gap={1} ml={2}>
                {radioValue === 'User' && (
                  <Stack flexDirection="row" gap={1}>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      sx={{ width: 400 }}
                      label="User email"
                      value={userEmail}
                      onChange={(event) => {
                        setUserEmail(event.target.value);
                        setHasError(false);
                      }}
                      error={hasError}
                      helperText={
                        hasError ? 'Must be a valid email address.' : ''
                      }
                      disabled={disabledByRole}
                      autoComplete="off"
                    />
                    <Button
                      variant="contained"
                      loading={isUserPending}
                      disabled={
                        disabledByRole ||
                        (radioValue === 'User' && !userEmail) ||
                        isUserPending
                      }
                      onClick={handleUserIndexing}
                      sx={{ height: 40 }}
                    >
                      Index
                    </Button>
                  </Stack>
                )}

                {radioValue === 'User' && <Box height={hasError ? 20 : 40} />}

                {radioValue === 'Domain' && (
                  <Stack flexDirection="row" gap={1}>
                    <Autocomplete
                      disableClearable
                      size="medium"
                      options={modifiedDomains}
                      autoComplete
                      autoHighlight
                      noOptionsText="No Domains Found"
                      sx={{ width: 400 }}
                      value={selectedDomain}
                      onChange={(_event, newValue) => {
                        setSelectedDomain(newValue);
                      }}
                      disabled={disabledByRole}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search for a domain"
                          fullWidth
                        />
                      )}
                      renderOption={(props, option) => (
                        <ListItem
                          {...props}
                          key={option}
                          secondaryAction={
                            <Tooltip title={'Number of users in domain'}>
                              <Chip
                                label={handleNumberOfUsers(
                                  allDomains.findIndex(
                                    (domain) => domain === option
                                  ),
                                  option === 'All'
                                )}
                                size="small"
                                color="primary"
                              />
                            </Tooltip>
                          }
                        >
                          {option}
                        </ListItem>
                      )}
                    />
                    <Button
                      variant="contained"
                      loading={fakeLoading || userIndexingIsDisabled}
                      disabled={
                        disabledByRole || userIndexingIsDisabled || fakeLoading
                      }
                      onClick={handleDomainIndexing}
                      sx={{ height: 40 }}
                    >
                      Index
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Tooltip>
        </Stack>

        {selectedDomain !== 'All' && radioValue === 'Domain' && (
          <Box sx={{ height: '40vh', mx: 'auto', mt: 1 }}>
            <SharedDataGridClient
              columns={columns}
              rows={indexUserLogs || []}
              slots={{
                toolbar: CustomToolbar,
                ...createGridOverlays('User Indexing Logs', <PersonOff />),
              }}
              getRowId={(row) => row.lastRunTime}
              loading={isIndexUserLogsLoading || isIndexUserLogsFetching}
            />
          </Box>
        )}
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (radioValue === 'User') {
            if (!emailRegex.test(userEmail)) {
              setHasError(true);
            } else {
              setHasError(false);
              userMutate();
            }
          } else {
            setFakeLoading(true);
            if (selectedDomain === 'All') {
              mutateAllUsersAllDomains();
            } else if (selectedDomain) {
              mutate();
            }
          }
        }}
        mutateLoading={isPending || isUserPending || isAllUsersPending}
      />
    </Stack>
  );
}
